/**
 * Hooks to fetch and update the current user's organisation details (name, ABN, etc.).
 * Uses the organisationId from the authenticated user's context.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organisationsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Organisation, UpdateOrganisationInput } from '../types';

export function useCurrentOrganisation() {
  const { user } = useAuth();
  const orgId = user?.organisationId;

  return useQuery<Organisation>({
    queryKey: ['organisation', orgId],
    queryFn: () => organisationsApi.get(orgId!),
    enabled: !!orgId,
  });
}

export function useUpdateCurrentOrganisation() {
  const { user } = useAuth();
  const orgId = user?.organisationId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateOrganisationInput) =>
      organisationsApi.update(orgId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisation', orgId] });
    },
  });
}
